/**
 * NutSheetHeader — title row, location dropdown, date picker.
 *
 * Title pattern: "<Location Name> Retail - New and Used Transaction Sheet — <Date>"
 */

import React from 'react';
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { LocationDropdown, INDIVIDUAL_LOCATIONS } from '../LocationDropdown';

interface NutSheetHeaderProps {
  locationId: number;
  date: string;
  onLocationChange: (locationId: number) => void;
  onDateChange: (date: string) => void;
  onPrint: () => void;
  onExport: () => void;
}

function formatDate(date: string): string {
  // Parse YYYY-MM-DD as a local date (no timezone shift).
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Shift a YYYY-MM-DD date by whole days, staying in local time so DST/timezone
// never bumps it to the wrong calendar day. Returns YYYY-MM-DD.
function shiftDate(date: string, deltaDays: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + deltaDays);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export const NutSheetHeader: React.FC<NutSheetHeaderProps> = ({
  locationId,
  date,
  onLocationChange,
  onDateChange,
  onPrint,
  onExport,
}) => {
  const locationName =
    INDIVIDUAL_LOCATIONS.find((l) => l.id === locationId)?.name ?? 'Location';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {locationName} Retail — New and Used Transaction Sheet
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-gray-500">{formatDate(date)}</p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onDateChange(shiftDate(date, -1))}
                aria-label="Previous day"
                title="Previous day"
                className="flex items-center gap-0.5 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <span aria-hidden="true">‹</span> Prev
              </button>
              <button
                type="button"
                onClick={() => onDateChange(shiftDate(date, 1))}
                aria-label="Next day"
                title="Next day"
                className="flex items-center gap-0.5 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                Next <span aria-hidden="true">›</span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Location
            </label>
            <LocationDropdown value={locationId} onChange={onLocationChange} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <PrinterIcon className="h-4 w-4" />
            Print PDF
          </button>
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>
    </div>
  );
};
